import { useId } from "react";
import { useTranslation } from "react-i18next";
import { IconChevronDown, IconLoader2 } from "@tabler/icons-react";
import { cn } from "../../lib/utils";

/**
 * A titled section that folds (Figma "UX/UI Desktop Version", the context
 * panel's "section-progression" / "Sources Header"): the title, an optional
 * count and spinner beside it, and a chevron that turns when the body opens.
 *
 * Controlled: the owner decides what is open, because something outside the
 * section can open it (the answer's "3 searches · 9 sources" opens the
 * panel's Sources). Sections stack with a hairline between them, drawn by the
 * section itself so a list of them needs no separator of its own.
 */
export function DisclosureSection({
  title,
  count,
  loading,
  isOpen,
  onToggle,
  children,
}: {
  title: string;
  // Shown beside the title (the number of sources), or null.
  count: number | null;
  // Spins beside the title while the body is still on its way.
  loading: boolean;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}): React.JSX.Element {
  const { t } = useTranslation();
  const bodyId = useId();
  return (
    <section className="flex flex-col border-t border-ds-slate-100 py-3 first:border-t-0 first:pt-0 last:pb-0">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={bodyId}
        className="flex h-6 w-full cursor-pointer items-center justify-between gap-2 border-none bg-transparent p-0 text-left"
      >
        <span className="flex min-w-0 items-baseline gap-2">
          <span className="truncate font-dm-sans text-ui-body font-bold text-ds-slate-950">
            {title}
          </span>
          {count !== null && (
            <span className="font-dm-sans text-xs text-ds-slate-700">
              {count}
            </span>
          )}
          {loading && (
            <IconLoader2
              className="size-3.5 shrink-0 animate-spin self-center text-ds-slate-700"
              aria-label={t("Loading…")}
            />
          )}
        </span>
        <IconChevronDown
          stroke={1.5}
          className={cn(
            "size-4 shrink-0 text-ds-slate-500 transition-transform duration-150 ease-out",
            isOpen && "rotate-180"
          )}
        />
      </button>
      {isOpen && (
        <div id={bodyId} className="pt-3">
          {children}
        </div>
      )}
    </section>
  );
}
